import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { SystemPartner } from 'api/types';
import { useUserContext } from 'context/UserContextProvider';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import { useDeleteWorkshopMutation } from 'hooks/mutations/useDeleteWorkshopMutation';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MdDeleteForever } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import { DangerZoneRow } from './DangerZoneRow';

type Props = {
  workshopId: number;
  systemPartnersOfWorkshop: SystemPartner[];
};

export const HardDelete = ({ workshopId, systemPartnersOfWorkshop }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast(defaultToastOptions);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useUserContext();
  const workshopQuery = useWorkshopQuery(workshopId);
  const hardDeleteWorkshopMutation = useDeleteWorkshopMutation(workshopId, 'hard');
  const isSubscriptionBlockingSoftDelete = workshopQuery.data?.subscription != null && !workshopQuery.data?.in_free_trial;
  const canBeHardDeleted = workshopQuery.data?.can_be_hard_deleted === true;

  const bodyTexts = useMemo<string[]>(() => {
    let texts: string[] = [];
    if (!isSubscriptionBlockingSoftDelete && canBeHardDeleted) {
      texts.push(t('workshop:hard_delete.body.default'));
    } else {
      if (isSubscriptionBlockingSoftDelete) {
        texts.push(t('workshop:hard_delete.body.has_subscription'));
      }
      if (!canBeHardDeleted) {
        texts.push(t('workshop:hard_delete.body.can_not_be_hard_deleted'));
      }
    }
    return texts;
  }, [canBeHardDeleted, isSubscriptionBlockingSoftDelete, t]);

  const disabledTooltip = useMemo<string | null>(() => {
    if (!user.permissions.includes('workshops:hard-delete')) {
      return t('workshop:hard_delete.tooltip.disabled.no_permission');
    }
    if (isSubscriptionBlockingSoftDelete) {
      return t('workshop:hard_delete.tooltip.disabled.has_subscription');
    }
    return null;
  }, [isSubscriptionBlockingSoftDelete, t, user.permissions]);

  return (
    <>
      <DangerZoneRow>
        <Stack gap={0}>
          <Heading size="xxs" as="h3">
            {t('workshop:hard_delete.title')}
          </Heading>
          {bodyTexts.map((text, index) => (
            <Text key={index}>{text}</Text>
          ))}
        </Stack>
        {canBeHardDeleted && (
          <Tooltip label={disabledTooltip} hasArrow>
            <Button
              leftIcon={<MdDeleteForever size="24px" />}
              minWidth="24ch"
              colorScheme="red"
              onClick={onOpen}
              isLoading={hardDeleteWorkshopMutation.isPending}
              loadingText={t('workshop:hard_delete.default.button')}
              disabled={disabledTooltip != null || hardDeleteWorkshopMutation.isPending}
            >
              {t('workshop:hard_delete.default.button')}
            </Button>
          </Tooltip>
        )}
      </DangerZoneRow>

      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('workshop:hard_delete.modal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              <Trans i18nKey="workshop:hard_delete.modal.body.part_1" />
            </Text>
            <Text py="4">
              {systemPartnersOfWorkshop.length > 0 && (
                <Trans
                  i18nKey="workshop:hard_delete.modal.body.part_2_with_spf_user"
                  values={{ systemPartnerNames: systemPartnersOfWorkshop.map((partner) => partner.name!).join(', ') }}
                />
              )}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              {t('workshop:hard_delete.modal.button_secondary')}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={() => {
                hardDeleteWorkshopMutation.mutate(undefined, {
                  onSuccess: (data) => {
                    onClose();
                    queryClient.invalidateQueries({ queryKey: useWorkshopQuery.getKey(workshopId) });
                    // Empty response means the workshop has been deleted immediately
                    if (data.data == null) {
                      toast({
                        title: t('workshop:hard_delete.toast.success.title'),
                        status: 'success',
                      });
                      navigate(`/app/op/workshops`);
                    } else {
                      toast({
                        title: t('workshop:hard_delete.toast.scheduled.title'),
                        description: t('workshop:hard_delete.toast.scheduled.description'),
                        status: 'info',
                      });
                    }
                  },
                  onError: (error) => {
                    toast({
                      title: error.message ?? t('errors:general'),
                      status: 'error',
                    });
                  },
                });
              }}
              isLoading={hardDeleteWorkshopMutation.isPending}
              loadingText={t('workshop:hard_delete.modal.button_primary')}
            >
              {t('workshop:hard_delete.modal.button_primary')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
